import React, { useState } from 'react';

const AdminHome = ({ member, setActivePage }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventList, setShowEventList] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    name: '', location: '', date: '', call_time: '06:00',
    call_time_announced: false,
  });

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getEventsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const handleDayClick = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayEvents = getEventsForDate(day);
    if (dayEvents.length > 0) {
      setSelectedDate(dateStr);
      setShowEventList(true);
    }
  };

  const handleAddEvent = () => {
    if (!newEvent.name || !newEvent.location || !newEvent.date) {
      alert('Please fill in all required fields!');
      return;
    }
    const event = {
      id: Date.now(),
      ...newEvent,
      performers: { male_movers: [], female_movers: [], instrumentalists: [] }
    };
    setEvents([...events, event]);
    setNewEvent({ name: '', location: '', date: '', call_time: '06:00', call_time_announced: false });
    setShowAddEvent(false);
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);

  const today = new Date();
  const isToday = (day) =>
    day === today.getDate() &&
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear();

  return (
    <div style={styles.container}>
      {/* Welcome Banner */}
      <div style={styles.banner}>
        <div>
          <h2 style={styles.bannerTitle}>
            Welcome back, {member?.first_name}! 👋
          </h2>
          <p style={styles.bannerSub}>
            Dulimbay Cultural Performing Group — Admin Panel
          </p>
        </div>
        <div style={styles.bannerDate}>
          <p style={styles.bannerDateText}>
            {today.toLocaleDateString('en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.cardsRow}>
        <div style={{ ...styles.card, borderTop: '4px solid #3498db' }}
          onClick={() => setActivePage('members')}>
          <p style={styles.cardIcon}>👥</p>
          <p style={styles.cardValue}>0</p>
          <p style={styles.cardLabel}>Total Members</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #e74c3c' }}
          onClick={() => setActivePage('members')}>
          <p style={styles.cardIcon}>⏳</p>
          <p style={styles.cardValue}>0</p>
          <p style={styles.cardLabel}>Pending Approvals</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #f39c12' }}
          onClick={() => setActivePage('costumes')}>
          <p style={styles.cardIcon}>👘</p>
          <p style={styles.cardValue}>0</p>
          <p style={styles.cardLabel}>Borrowed Costumes</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #9b59b6' }}
          onClick={() => setActivePage('instruments')}>
          <p style={styles.cardIcon}>🎵</p>
          <p style={styles.cardValue}>0</p>
          <p style={styles.cardLabel}>Borrowed Instruments</p>
        </div>
        <div style={{ ...styles.card, borderTop: '4px solid #2ecc71' }}
          onClick={() => setActivePage('fines')}>
          <p style={styles.cardIcon}>💰</p>
          <p style={styles.cardValue}>₱0.00</p>
          <p style={styles.cardLabel}>Total Unpaid Fines</p>
        </div>
      </div>

      {/* Calendar + Upcoming Events */}
      <div style={styles.bottomRow}>
        {/* Calendar */}
        <div style={styles.calendarBox}>
          <div style={styles.calendarHeader}>
            <button style={styles.calNavBtn} onClick={prevMonth}>◀</button>
            <h3 style={styles.calTitle}>
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button style={styles.calNavBtn} onClick={nextMonth}>▶</button>
          </div>

          {/* Add Event Button */}
          <button style={styles.addEventBtn} onClick={() => setShowAddEvent(true)}>
            + Add Performance / Event
          </button>

          {/* Day Labels */}
          <div style={styles.dayLabels}>
            {days.map(d => (
              <div key={d} style={styles.dayLabel}>{d}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={styles.calGrid}>
            {/* Empty cells for first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} style={styles.emptyCell} />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDate(day);
              const hasEvents = dayEvents.length > 0;
              return (
                <div
                  key={day}
                  style={{
                    ...styles.dayCell,
                    ...(isToday(day) ? styles.todayCell : {}),
                    ...(hasEvents ? styles.eventCell : {}),
                  }}
                  onClick={() => handleDayClick(day)}
                >
                  <span style={isToday(day) ? styles.todayNum : styles.dayNum}>
                    {day}
                  </span>
                  {hasEvents && (
                    <div style={styles.dotsRow}>
                      {dayEvents.slice(0, 3).map((_, idx) => (
                        <div key={idx} style={styles.redDot} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Events */}
        <div style={styles.upcomingBox}>
          <h3 style={styles.upcomingTitle}>📅 Upcoming Performances</h3>
          {events.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>🎭</p>
              <p style={styles.emptyText}>No upcoming performances yet!</p>
              <p style={styles.emptySubText}>Click "Add Performance / Event" to add one.</p>
            </div>
          ) : (
            events
              .filter(e => new Date(e.date) >= new Date())
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .slice(0, 5)
              .map(event => (
                <div key={event.id} style={styles.eventItem}
                  onClick={() => { setSelectedEvent(event); }}>
                  <div style={styles.eventDateBox}>
                    <span style={styles.eventMonth}>
                      {new Date(event.date).toLocaleDateString('en-PH', { month: 'short' })}
                    </span>
                    <span style={styles.eventDay}>
                      {new Date(event.date).getDate()}
                    </span>
                  </div>
                  <div style={styles.eventInfo}>
                    <p style={styles.eventName}>{event.name}</p>
                    <p style={styles.eventLocation}>📍 {event.location}</p>
                    <p style={styles.eventTime}>
                      ⏰ {event.call_time_announced ? 'To be announced' : event.call_time}
                    </p>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Add Event Modal */}
      {showAddEvent && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🎭 Add Performance / Event</h3>

            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Event Name *</label>
              <input style={styles.modalInput} type="text"
                placeholder="ex. Kadayawan Festival 2024"
                value={newEvent.name}
                onChange={e => setNewEvent({ ...newEvent, name: e.target.value })} />
            </div>

            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Location *</label>
              <input style={styles.modalInput} type="text"
                placeholder="ex. CSU Main Campus, Ampayon"
                value={newEvent.location}
                onChange={e => setNewEvent({ ...newEvent, location: e.target.value })} />
            </div>

            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Date *</label>
              <input style={styles.modalInput} type="date"
                value={newEvent.date}
                onChange={e => setNewEvent({ ...newEvent, date: e.target.value })} />
            </div>

            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Call Time</label>
              <div style={styles.callTimeRow}>
                <input
                  style={{ ...styles.modalInput, flex: 1 }}
                  type="time"
                  value={newEvent.call_time}
                  disabled={newEvent.call_time_announced}
                  onChange={e => setNewEvent({ ...newEvent, call_time: e.target.value })} />
                <label style={styles.announceLabel}>
                  <input type="checkbox"
                    checked={newEvent.call_time_announced}
                    onChange={e => setNewEvent({ ...newEvent, call_time_announced: e.target.checked })} />
                  <span style={{ marginLeft: '6px' }}>To be announced</span>
                </label>
              </div>
            </div>

            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowAddEvent(false)}>
                Cancel
              </button>
              <button style={styles.doneBtn} onClick={handleAddEvent}>
                Done ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event List Modal */}
      {showEventList && selectedDate && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              📅 Events on {new Date(selectedDate).toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' })}
            </h3>
            {events
              .filter(e => e.date === selectedDate)
              .map(event => (
                <div key={event.id} style={styles.eventListItem}
                  onClick={() => { setSelectedEvent(event); setShowEventList(false); }}>
                  <p style={styles.eventListName}>🎭 {event.name}</p>
                  <p style={styles.eventListDetail}>📍 {event.location}</p>
                  <p style={styles.eventListDetail}>
                    ⏰ {event.call_time_announced ? 'To be announced' : event.call_time}
                  </p>
                </div>
              ))}
            <button style={styles.cancelBtn} onClick={() => setShowEventList(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🎭 {selectedEvent.name}</h3>
            <div style={styles.eventDetail}>
              <p style={styles.detailRow}>📍 <strong>Location:</strong> {selectedEvent.location}</p>
              <p style={styles.detailRow}>📅 <strong>Date:</strong> {new Date(selectedEvent.date).toLocaleDateString('en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p style={styles.detailRow}>⏰ <strong>Call Time:</strong> {selectedEvent.call_time_announced ? 'To be announced' : selectedEvent.call_time}</p>
            </div>

            <div style={styles.performerSection}>
              <h4 style={styles.performerTitle}>👥 Performers</h4>
              <div style={styles.performerGroups}>
                <div style={styles.performerGroup}>
                  <p style={styles.performerGroupTitle}>🕺 Male Movers</p>
                  {selectedEvent.performers.male_movers.length === 0
                    ? <p style={styles.noPerformer}>Not assigned yet</p>
                    : selectedEvent.performers.male_movers.map((p, i) => (
                      <p key={i} style={styles.performerName}>{p}</p>
                    ))}
                </div>
                <div style={styles.performerGroup}>
                  <p style={styles.performerGroupTitle}>💃 Female Movers</p>
                  {selectedEvent.performers.female_movers.length === 0
                    ? <p style={styles.noPerformer}>Not assigned yet</p>
                    : selectedEvent.performers.female_movers.map((p, i) => (
                      <p key={i} style={styles.performerName}>{p}</p>
                    ))}
                </div>
                <div style={styles.performerGroup}>
                  <p style={styles.performerGroupTitle}>🎵 Instrumentalists</p>
                  {selectedEvent.performers.instrumentalists.length === 0
                    ? <p style={styles.noPerformer}>Not assigned yet</p>
                    : selectedEvent.performers.instrumentalists.map((p, i) => (
                      <p key={i} style={styles.performerName}>{p}</p>
                    ))}
                </div>
              </div>
            </div>

            <button style={styles.cancelBtn} onClick={() => setSelectedEvent(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  banner: {
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    borderRadius: '16px',
    padding: '25px 30px',
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  bannerTitle: { color: 'white', margin: '0 0 5px 0', fontSize: '22px' },
  bannerSub: { color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '13px' },
  bannerDate: { textAlign: 'right' },
  bannerDateText: { color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '13px' },
  cardsRow: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  card: {
    flex: '1',
    minWidth: '140px',
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  cardIcon: { fontSize: '28px', margin: '0 0 8px 0' },
  cardValue: { fontSize: '24px', fontWeight: 'bold', color: '#333', margin: '0 0 5px 0' },
  cardLabel: { fontSize: '12px', color: '#888', margin: 0 },
  bottomRow: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  calendarBox: {
    flex: '1',
    minWidth: '300px',
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '15px',
  },
  calNavBtn: {
    background: '#f0f2f5',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  calTitle: { margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#333' },
  addEventBtn: {
    width: '100%',
    padding: '10px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    marginBottom: '15px',
  },
  dayLabels: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: '5px',
  },
  dayLabel: {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#888',
    padding: '5px 0',
  },
  calGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
  emptyCell: { height: '44px' },
  dayCell: {
    height: '44px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  todayCell: {
    background: '#0f3460',
  },
  eventCell: {
    cursor: 'pointer',
  },
  dayNum: {
    fontSize: '13px',
    color: '#333',
  },
  todayNum: {
    fontSize: '13px',
    color: 'white',
    fontWeight: 'bold',
  },
  dotsRow: {
    display: 'flex',
    gap: '2px',
    marginTop: '2px',
  },
  redDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    background: '#e74c3c',
  },
  upcomingBox: {
    width: '300px',
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    height: 'fit-content',
  },
  upcomingTitle: {
    margin: '0 0 15px 0',
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#333',
  },
  emptyState: { textAlign: 'center', padding: '20px 0' },
  emptyIcon: { fontSize: '40px', margin: '0 0 10px 0' },
  emptyText: { color: '#666', margin: '0 0 5px 0', fontSize: '14px' },
  emptySubText: { color: '#aaa', margin: 0, fontSize: '12px' },
  eventItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    borderRadius: '10px',
    marginBottom: '10px',
    background: '#f8f9fa',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  eventDateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f3460',
    borderRadius: '8px',
    padding: '8px',
    minWidth: '45px',
  },
  eventMonth: { color: 'rgba(255,255,255,0.7)', fontSize: '10px', textTransform: 'uppercase' },
  eventDay: { color: 'white', fontSize: '20px', fontWeight: 'bold' },
  eventInfo: { flex: 1 },
  eventName: { margin: '0 0 4px 0', fontWeight: 'bold', color: '#333', fontSize: '13px' },
  eventLocation: { margin: '0 0 2px 0', color: '#888', fontSize: '12px' },
  eventTime: { margin: 0, color: '#888', fontSize: '12px' },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 1001,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    padding: '30px',
    width: '100%',
    maxWidth: '460px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
  },
  modalField: { marginBottom: '15px' },
  modalLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#444',
    marginBottom: '6px',
  },
  modalInput: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: '1.5px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  callTimeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  announceLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: '#555',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelBtn: {
    flex: 1,
    padding: '12px',
    background: 'white',
    color: '#666',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  doneBtn: {
    flex: 1,
    padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  eventListItem: {
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
  },
  eventListName: {
    margin: '0 0 5px 0',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '14px',
  },
  eventListDetail: {
    margin: '0 0 3px 0',
    color: '#888',
    fontSize: '13px',
  },
  eventDetail: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '15px',
    marginBottom: '15px',
  },
  detailRow: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#444',
  },
  performerSection: { marginBottom: '20px' },
  performerTitle: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 12px 0',
  },
  performerGroups: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  performerGroup: {
    flex: 1,
    minWidth: '100px',
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '12px',
  },
  performerGroupTitle: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#555',
    margin: '0 0 8px 0',
  },
  noPerformer: {
    fontSize: '12px',
    color: '#aaa',
    margin: 0,
  },
  performerName: {
    fontSize: '12px',
    color: '#333',
    margin: '0 0 4px 0',
  },
};

export default AdminHome;